/**
 * Appointment API-to-Client Adapter
 * 
 * Transforms AppointmentDto from API into simplified ClientAppointment
 */

import { AddressDtoTypeEnum, AppointmentDto, ContinuousAppointmentDto, PatientDto } from "../../../../../api-client";
import { ClientAppointment, ClientContinuousAppointment } from "../types";
import { adaptTimeSlotFromApi } from "./timeslot.adapter";
import { adaptAddressFromApi } from "./address.adapter";
import { adaptPatientFromApi } from "./patient.adapter";
import notificationStore from "../../../../../stores/Notification/notificationStore";

/**
 * Tracks which appointments we have already notified the user about during this session,
 * so a single broken row doesn't fire a toast on every re-render. The adapter is invoked
 * many times per data refresh (e.g. once per participant chip in EventContent).
 */
const warnedAppointmentIds = new Set<string>();

const notifyMissingPatientData = (
  kind: "Appointment" | "Continuous Appointment",
  appointmentId: string | undefined,
  firstSlotId: string | undefined,
  firstSlotType: string | undefined,
  raw: unknown,
): void => {
  const key = `${kind}:${appointmentId ?? "<no-id>"}:${firstSlotId ?? "<no-slot>"}`;
  const consoleMessage = `${kind} ${appointmentId ?? "<unknown>"} (timeSlot ${firstSlotId ?? "<unknown>"}, type=${firstSlotType ?? "<none>"}) has no patient data; rendering without patient.`;
  console.warn(consoleMessage, raw);

  if (warnedAppointmentIds.has(key)) return;
  warnedAppointmentIds.add(key);

  // notificationStore.showMessage(
  //   `⚠️ Termin ${appointmentId ?? "?"} (Zeitslot ${firstSlotId ?? "?"}) wird ohne Patientendaten angezeigt. Bitte Datensatz prüfen.`,
  //   "warning",
  // );
};


/**
 * Predicate for "is this AppointmentDto a cancelled appointment?".
 *
 * The backend `getAppointmentsByDateAndLocation` endpoint mixes ACTIVE and
 * CANCELLED appointments into the same response. Cancelled ones come from the
 * CancelledAppointment table and are marked by the backend via:
 *   - `originalDate` set (the original scheduled date pre-cancellation)
 *   - `resolved === false` (only unresolved cancellations are returned)
 * Both origins are delivered — `byTherapist` only labels who cancelled
 * (patient-initiated ones additionally surface on the practice worklist).
 *
 * Older code used `appointment.end === null` as a cancellation marker, but the
 * current backend does not null out `end` on cancellation, so that check
 * silently became a no-op (and broke the "Stornierungen" toggle).
 */
export const isCancelledAppointment = (appointment: Pick<AppointmentDto, "originalDate" | "resolved">): boolean => {
  return !!appointment.originalDate && appointment.resolved === false;
};


const apiVisitTypeToVisitType = (apiType: AddressDtoTypeEnum  | undefined) => {
  switch(apiType) {
    case AddressDtoTypeEnum.Haus:
      return 'haus';
    case AddressDtoTypeEnum.Heim:
      return 'heim';
    case AddressDtoTypeEnum.Praxis:
      return 'praxis';
    default:
      return undefined;
  }
};


/**
 * Transform API AppointmentDto to simplified ClientAppointment
 * Do not use with continuous appointments
 */
export const adaptAppointmentFromApi = (appointment: AppointmentDto): ClientAppointment => {
  // Handle raw timeslot objects that may be passed as participants (before backend wraps them).
  // A raw timeslot has `type` directly on it but no `timeSlots` array.
  const isRawTimeSlot = !appointment.timeSlots && (appointment as any).type;
  const effectiveAppointment = isRawTimeSlot
    ? { ...appointment, timeSlots: [{ ...(appointment as any) }] }
    : appointment;

  const visitType = effectiveAppointment.address?.type;
  const timeSlots = (effectiveAppointment.timeSlots || []).map(ts => adaptTimeSlotFromApi(ts, false, apiVisitTypeToVisitType(visitType)));

  let patientDto = effectiveAppointment.timeSlots?.[0]?.therapyRx?.rx?.patient as PatientDto;

  if (!patientDto) {
    const firstSlot = effectiveAppointment.timeSlots?.[0];
    const firstSlotType = firstSlot?.type;
    if (firstSlotType === "GroupTimeSlotPatient") {
      // Try top-level patient on the slot (custom backend field), then participant's nested patient
      patientDto =
        ((firstSlot as any)?.patient as PatientDto) ||
        (effectiveAppointment.participants?.[0]?.timeSlots?.[0]?.therapyRx?.rx?.patient as PatientDto);
    } else if (firstSlotType !== "GroupTimeSlotTherapist") {
      notifyMissingPatientData(
        "Appointment",
        effectiveAppointment.id,
        (firstSlot as any)?.id,
        firstSlotType,
        effectiveAppointment,
      );
    }
  }

  const patient = patientDto ? adaptPatientFromApi(patientDto) : undefined;
  
  // Compute derived properties
  const starts = timeSlots.map(ts => ts.start);
  const ends = timeSlots.map(ts => ts.end);
  const start = new Date(Math.min(...starts.map(d => d.getTime())));
  const end = new Date(Math.max(...ends.map(d => d.getTime())));
  
  const therapyAbbreviations = timeSlots
    .map(ts => ts.therapy?.abbreviation)
    .filter(Boolean) as string[];
  
  const containsHeatTreatment = timeSlots.some(ts => ts.therapy?.isHeatTreatment);
  const hasNotes = Boolean(
    (effectiveAppointment as any).hasNotes ||
    (effectiveAppointment.notes?.length || 0) > 0
  );
  const isCancelled = isCancelledAppointment(effectiveAppointment);
  const reason = effectiveAppointment.reason || undefined;
  
  const hasMultipleTimeSlots =
    (effectiveAppointment as any).hasMultipleTimeSlots ?? timeSlots.length > 1;

  return {
    id: effectiveAppointment.id!,
    start,
    end,
    patient: patient!,
    participants: effectiveAppointment.participants || [],
    timeSlots,
    address: effectiveAppointment.address ? adaptAddressFromApi(effectiveAppointment.address) : undefined,
    attended: effectiveAppointment.attended,
    frequency: effectiveAppointment.frequency,
    hasMultipleTimeSlots,
    displayTitle: patient
      ? patient.displayName
      : effectiveAppointment.timeSlots?.[0]?.type === "GroupTimeSlotTherapist"
      ? "TherapistGroupAppointment"
      : "—",
    therapyAbbreviations,
    containsHeatTreatment,
    hasNotes,
    isCancelled,
    reason
  };
};

export const adaptContinuousAppointmentFromApi = (appointment: ContinuousAppointmentDto): ClientContinuousAppointment => {
  const visitType = appointment.address?.type;
  const timeSlots = (appointment.timeSlots || []).map(ts => adaptTimeSlotFromApi(ts,true, apiVisitTypeToVisitType(visitType)));

  let patientDto = appointment.timeSlots?.[0]?.therapyRx?.rx?.patient as PatientDto;

  if (!patientDto) {
    const firstSlot = appointment.timeSlots?.[0];
    const firstSlotType = firstSlot?.type;
    if (
      firstSlotType === "ContinuousGroupTimeSlotTherapist" ||
      firstSlotType === "ContinuousGroupTimeSlotPatient"
    ) {
      patientDto =
        ((firstSlot as any)?.patient as PatientDto) ||
        (appointment.participants?.[0]?.timeSlots?.[0]?.therapyRx?.rx?.patient as PatientDto);
    } else {
      // Data anomaly: degrade gracefully instead of throwing and crashing the calendar.
      notifyMissingPatientData(
        "Continuous Appointment",
        appointment.id,
        (firstSlot as any)?.id,
        firstSlotType,
        appointment,
      );
    }
  }

  const patient = patientDto ? adaptPatientFromApi(patientDto) : undefined;
  
  // Compute derived properties
  const starts = timeSlots.map(ts => ts.start);
  const ends = timeSlots.map(ts => ts.end);
  const start = new Date(Math.min(...starts.map(d => d.getTime())));
  const end = new Date(Math.max(...ends.map(d => d.getTime())));
  
  const therapyAbbreviations = timeSlots
    .map(ts => ts.therapy?.abbreviation)
    .filter(Boolean) as string[];
  
  const containsHeatTreatment = timeSlots.some(ts => ts.therapy?.isHeatTreatment);
  const hasNotes = timeSlots.some(ts => ts.hasNotes);
  // ContinuousAppointmentDto does not carry the cancellation fields used for
  // one-off appointments; cancellations of continuous appointments are surfaced
  // through a different flow.
  const isCancelled = false;
  
  return {
    id: appointment.id!,
    start,
    end,
    patient: patient!,
    participants: appointment.participants || [],
    timeSlots,
    address: appointment.address ? adaptAddressFromApi(appointment.address) : undefined,
    frequency: appointment.frequency,
    hasMultipleTimeSlots: timeSlots.length > 1,
    displayTitle: patient
      ? patient.displayName
      : appointment.timeSlots?.[0]?.type === "ContinuousGroupTimeSlotTherapist"
      ? "TherapistGroupAppointment"
      : "—",
    therapyAbbreviations,
    containsHeatTreatment,
    hasNotes,
    isCancelled
  };
}